import {
  createWhatsappBoldRegex,
  createWhatsappItalicRegex,
  createWhatsappStrikeRegex,
} from './whatsappFormattingRegex';

const codeBlockTokenPrefix = '\uE000WB';
const inlineCodeTokenPrefix = '\uE000WI';
const protectedTokenSuffix = '\uE001';

function protect(
  values: string[],
  value: string,
  render: (content: string) => string,
  prefix: string,
) {
  const index = values.push(render(value)) - 1;
  return `${prefix}${index}${protectedTokenSuffix}`;
}

function restoreProtectedValues(
  html: string,
  values: string[],
  prefix: string,
) {
  const tokenRegex = new RegExp(`${prefix}(\\d+)${protectedTokenSuffix}`, 'g');
  return html.replace(tokenRegex, (_match, index: string) => {
    return values[Number(index)] ?? '';
  });
}

function formatInline(text: string) {
  const inlineCode: string[] = [];
  let html = text.replace(
    /`([^`\s](?:[^`]*[^`\s])?)`/g,
    (_match, content: string) =>
      protect(
        inlineCode,
        content,
        value => `<code>${value}</code>`,
        inlineCodeTokenPrefix,
      ),
  );

  html = html
    .replace(createWhatsappBoldRegex(), '<strong>$1</strong>')
    .replace(createWhatsappItalicRegex(), '<em>$1</em>')
    .replace(createWhatsappStrikeRegex(), '<s>$1</s>');

  return restoreProtectedValues(html, inlineCode, inlineCodeTokenPrefix);
}

function formatLines(text: string) {
  const lines = text.split('\n');
  const output: Array<{ block: boolean; html: string }> = [];

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const bullet = /^(?:-|\*)\s+(.+)$/.exec(line);
    const ordered = /^\d+\.\s+(.+)$/.exec(line);
    const codeBlock = new RegExp(
      `^${codeBlockTokenPrefix}\\d+${protectedTokenSuffix}$`,
    ).test(line);

    if (codeBlock) {
      output.push({ block: true, html: line });
    } else if (bullet || ordered) {
      const listTag = bullet ? 'ul' : 'ol';
      const items: string[] = [];

      while (index < lines.length) {
        const itemMatch = bullet
          ? /^(?:-|\*)\s+(.+)$/.exec(lines[index])
          : /^\d+\.\s+(.+)$/.exec(lines[index]);
        if (!itemMatch) break;
        items.push(`<li>${formatInline(itemMatch[1])}</li>`);
        index += 1;
      }

      index -= 1;
      output.push({
        block: true,
        html: `<${listTag}>${items.join('')}</${listTag}>`,
      });
    } else {
      const quote = /^&gt;\s?(.*)$/.exec(line);
      if (quote) {
        const quotedLines: string[] = [];

        while (index < lines.length) {
          const quoteMatch = /^&gt;\s?(.*)$/.exec(lines[index]);
          if (!quoteMatch) break;
          quotedLines.push(formatInline(quoteMatch[1]));
          index += 1;
        }

        index -= 1;
        output.push({
          block: true,
          html: `<blockquote>${quotedLines.join('<br>')}</blockquote>`,
        });
      } else {
        output.push({ block: false, html: formatInline(line) });
      }
    }
  }

  return output
    .map((segment, index) => {
      const previous = output[index - 1];
      const separator =
        previous && !previous.block && !segment.block ? '<br>' : '';
      return `${separator}${segment.html}`;
    })
    .join('');
}

export function whatsappToHtml(text: string): string {
  if (!text) return '';

  const codeBlocks: string[] = [];
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');

  html = html.replace(/```\n?([\s\S]*?)\n?```/g, (_match, content: string) => {
    return protect(
      codeBlocks,
      content,
      value => `<pre><code>${value}</code></pre>`,
      codeBlockTokenPrefix,
    );
  });

  html = formatLines(html);

  return restoreProtectedValues(html, codeBlocks, codeBlockTokenPrefix);
}
